import { ROTATING_ZONE_TITLE_VARIANT } from '@/utils/experiments';
import { STORAGE_KEYS, retrieve, store } from '@/utils/storage';

export const ROTATION_INTERVAL_MS = 4000;

export const ROTATION_MODE = {
    STATIC: 'static',
    TIMED: 'timed',
    LAUNCH: 'launch'
} as const;

export type RotationMode = (typeof ROTATION_MODE)[keyof typeof ROTATION_MODE];
export type Direction = 'down' | 'up';
export type Transition = { from: number; direction: Direction };

// Fallback launch key for callers that don't pass a stable per-zone key.
const getListKey = (titles: string[]): string => titles.join('');

const readHistory = (): Record<string, string> => {
    const raw = retrieve(STORAGE_KEYS.ROTATING_TITLE_HISTORY, 'local');
    if (!raw) {
        return {};
    }
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

const pageLoadSelection = new Map<string, number>();

export const pickLaunchIndex = (titles: string[], zoneKey?: string): number => {
    const key = zoneKey || getListKey(titles);

    const cached = pageLoadSelection.get(key);
    if (cached !== undefined) {
        return cached;
    }

    const history = readHistory();
    const previousIndex = history[key] ? titles.indexOf(history[key]) : -1;
    const nextIndex = previousIndex === -1 ? 0 : (previousIndex + 1) % titles.length;

    history[key] = titles[nextIndex];
    store({ key: STORAGE_KEYS.ROTATING_TITLE_HISTORY, value: JSON.stringify(history) }, 'local');
    pageLoadSelection.set(key, nextIndex);

    return nextIndex;
};

// three modes of title rotation: static, timed, and launch
// static: no rotation — reduced motion, or no experiment assignment (default)
// timed: the title rotates every 4 seconds
// launch: a single title chosen once per app launch or refresh, shown statically
// modes will be cleaned after the experiment is over
export const resolveMode = (variant: string | undefined, prefersReducedMotion: boolean): RotationMode => {
    if (prefersReducedMotion) {
        return ROTATION_MODE.STATIC;
    }
    if (variant === ROTATING_ZONE_TITLE_VARIANT.LAUNCH) {
        return ROTATION_MODE.LAUNCH;
    }
    if (variant === ROTATING_ZONE_TITLE_VARIANT.TIMED) {
        return ROTATION_MODE.TIMED;
    }

    return ROTATION_MODE.STATIC;
};
